import { SortTableColumnDef } from '@shared/sort-table/sort-table.model'
import { CreditTowerDimensions, CreditTowerBounds } from './credit-tower.model'

export enum CashFlowColors {
  pmiers = 'rgb(1, 201, 109)', // green
  loss = 'rgb(200, 50, 50)', // red
  retention = 'rgb(16, 19, 20)', // dark grey
  impliedLimit = 'rgb(175, 29, 97)', // sage pink
  impliedRetention = 'rgb(52, 133, 162)', // sage blue
}

export const DEFAULT_RETENTION: StackedBarProps = {
  key: 'RETENTION',
  name: 'Retention',
  color: CashFlowColors.retention,
}

/** Per Tranche - key is tranche _id */
export type CashFlowsTrancheDataDict = Record<string, CashFlowsDatum[]>

export interface CashFlowsData {
  impliedRetention: MetricDatum[]
  impliedLimit: number // top value of qs
  lossData: MetricDatum[]
  pmiersData: MetricDatum[]
  trancheData: CashFlowsTrancheDataDict
  tickNums: number
  actualDate: string | null
  rawGrossData: CreditGrossLossScenarioDatum[]
  rawCededData: RawCededCashFlowDatum[]
  rawGseGrossData?: GseRawGrossDatum[]
}

export interface GseRawGrossDatum {
  forecast_month: number
  gross_loss: number
  cumulative_gross_loss: number
  starting_upb: number
  change_in_upb: number
  ercf_capital: number
}

export interface CreditGrossLossScenarioDatum {
  forecast_month: number
  forecast_month_string: string
  mi_loss: number
  earned_premium: number
  performing_capital_required: number
  nonperforming_capital_required: number
  day_30_delq_upb: number
  day_60_delq_upb: number
  day_90_delq_upb: number
  day_120_delq_upb: number
  day_150_delq_upb: number
  day_180_delq_upb: number
  credit_event_upb: number
  starting_iif: number
  starting_upb: number
  conditional_disposition_rate: number
  conditional_prepay_rate: number
  unscheduled_principal: number
  starting_rif: number
  return_period?: number
}
export interface CashFlowsDatum {
  forecastMonth: string
  attach: number
  limit: number
  pmiersCapital: number
}

export interface MetricDatum {
  forecastMonth: string
  value: number
}

export interface CashFlowsGraphData {
  impliedRetention: MetricDatumAsDate[]
  impliedLimit: number
  lossData: MetricDatumAsDate[]
  pmiersData: MetricDatumAsDate[]
  trancheStackedBarData: StackedBarData
  tickNums: number
  actualDate: Date | null
  rawGrossData: CreditGrossLossScenarioDatum[]
  rawCededData: RawCededCashFlowDatum[]
  rawGseGrossData: GseRawGrossDatum[]
}

export interface MetricDatumAsDate {
  forecastMonth: Date
  value: number
}

export interface RawCededCashFlowDatum {
  forecastMonth: number
  premium: number
  loss: number
  pmiersRelief: number
  costOfPmiers: number
  limitRemaining: number
  annualPremiumRate: number
  cedingCommission: number
  profitCommission: number
  grossMargin: number
  netMargin: number
  lossRatio: number
  cedingCommissionRate: number
  profitCommissionRate: number
  grossMarginRate: number
  netMarginRate: number
  roracMargin: number
  rorac: number
}

export interface AreaDatum {
  x: Date // forecastMonth
  y0: number // attach
  y1: number // attach + limit
}

export interface StackedBarData {
  props: StackedBarProps[]
  data: StackedBarDatum[]
  focused?: StackedBarDatum
}

export interface StackedBarProps {
  key: string // tranche id
  name: string // tranche name
  color: string // tranche color
}

export interface StackedBarDatum {
  x: Date // forecastMonth
  [key: string]: number | Date // [_id] : limit
}

export interface FocusedDatum {
  loss: MetricDatumAsDate
  pmiers: MetricDatumAsDate
  bar?: StackedBarDatum
}

export interface CashFlowsGraphProperties {
  svgDimensions: CreditTowerDimensions
  graphDimensions: CreditTowerDimensions
  bounds: CreditTowerBounds
}

export enum CashFlowsChartType {
  Area = 'Area',
  Bar = 'Bar',
}

export const CASH_FLOWS_CHART_TYPES: CashFlowsChartType[] = [
  CashFlowsChartType.Area,
  CashFlowsChartType.Bar,
]

export const PMI_QS_CHART_TYPES: CashFlowsChartType[] = [CashFlowsChartType.Area]

export enum CashFlowsAxisType {
  Currency = 'Currency',
  Percent = 'Percent',
}

export const CASH_FLOWS_AXIS_TYPES: CashFlowsAxisType[] = [
  CashFlowsAxisType.Currency,
  CashFlowsAxisType.Percent,
]

const DEFAULT_SVG_DIMENSIONS: CreditTowerDimensions = {
  height: 0,
  width: 0,
  margin: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  offset: {
    height: 1,
    width: 1,
  },
}

const DEFAULT_GRAPH_DIMENSIONS: CreditTowerDimensions = {
  height: 0,
  width: 0,
  margin: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  offset: {
    height: 0.85,
    width: 0.9,
  },
}

export const DEFAULT_CASH_FLOWS_GRAPH_PROPERTIES: CashFlowsGraphProperties = {
  svgDimensions: DEFAULT_SVG_DIMENSIONS,
  graphDimensions: DEFAULT_GRAPH_DIMENSIONS,
  bounds: {
    most: 0,
  },
}

export const DESIGN_CASH_FLOW_TABLE_COLUMNS: readonly SortTableColumnDef[] = [
  {
    id: 'name',
    label: 'Tranche/Transaction',
    valueType: 'text',
  },
  {
    id: 'expected_premium',
    label: 'Expected Premium',
    valueType: 'short-numeric',
  },
  {
    id: 'expected_loss',
    label: 'Expected Loss',
    valueType: 'short-numeric',
  },
  {
    id: 'ceded_margin',
    label: 'Ceded Margin',
    valueType: 'short-numeric',
  },
  {
    id: 'loss_ratio',
    label: 'Loss Ratio',
    valueType: 'percentage',
  },
  {
    id: 'pmiers_capital',
    label: 'PMIERS Capital',
    valueType: 'short-numeric',
  },
  {
    id: 'annual_return_pmiers_capital',
    label: 'Annual Return On PMIERS Capital',
    valueType: 'percentage',
  },
  {
    id: 'annual_premium',
    label: 'Annual Premium',
    valueType: 'percentage',
  },
] as const

export const SUBMISSION_CASH_FLOW_TABLE_COLUMNS: readonly SortTableColumnDef[] = [
  {
    id: 'name',
    label: 'Tranche/Transaction',
    valueType: 'text',
  },
  {
    id: 'expected_premium',
    label: 'Expected Premium',
    valueType: 'short-numeric',
  },
  {
    id: 'expected_loss',
    label: 'Expected Loss',
    valueType: 'short-numeric',
  },
  {
    id: 'ceded_margin',
    label: 'Ceded Margin',
    valueType: 'short-numeric',
  },
  {
    id: 'loss_ratio',
    label: 'Loss Ratio',
    valueType: 'percentage',
  },
  {
    id: 'pmiers_capital',
    label: 'PMIERS Capital',
    valueType: 'short-numeric',
  },
  {
    id: 'annual_premium',
    label: 'Annual Premium',
    valueType: 'percentage',
  },
  {
    id: 'rorac_margin',
    label: 'RORAC Margin',
    valueType: 'short-numeric',
  },
  {
    id: 'rorac',
    label: 'RORAC',
    valueType: 'percentage',
  },
] as const

export enum CreditRawCashFlowTab {
  Gross = 'Gross',
  Ceded = 'Ceded',
}

export const CREDIT_RAW_CASH_FLOW_TABS: CreditRawCashFlowTab[] = [
  CreditRawCashFlowTab.Gross,
  CreditRawCashFlowTab.Ceded,
]

const RAW_XOL_ILN_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  {
    id: 'premium',
    label: 'Premium',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'loss',
    label: 'Loss',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'limitRemaining',
    label: 'Limit Remaining',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'annualPremiumRate',
    label: 'Annual Premium Rate',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]
export const DESIGN_RAW_XOL_ILN_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  ...RAW_XOL_ILN_CEDED_CASH_FLOW_TABLE_COLUMNS,
  {
    id: 'pmiersRelief',
    label: 'PMIERS Relief',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'costOfPmiers',
    label: 'Cost of PMIERS',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]
export const SUBMISSION_RAW_XOL_ILN_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  ...RAW_XOL_ILN_CEDED_CASH_FLOW_TABLE_COLUMNS,
  {
    id: 'roracMargin',
    label: 'RORAC Margin',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'rorac',
    label: 'RORAC',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]

const RAW_QS_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  {
    id: 'premium',
    label: 'Premium',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'loss',
    label: 'Loss',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'cedingCommission',
    label: 'Ceding Commission',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '10rem',
  },
  {
    id: 'profitCommission',
    label: 'Profit Commission',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'grossMargin',
    label: 'Gross Margin',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'netMargin',
    label: 'Net Margin',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'lossRatio',
    label: 'Loss Ratio',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'cedingCommissionRate',
    label: 'Ceding Commission Rate',
    valueType: 'percentage',
    editable: false,
    minWidth: '12.5rem',
  },
  {
    id: 'profitCommissionRate',
    label: 'Profit Commission Rate',
    valueType: 'percentage',
    editable: false,
    minWidth: '12.5rem',
  },
  {
    id: 'grossMarginRate',
    label: 'Gross Margin Rate',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'netMarginRate',
    label: 'Net Margin Rate',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]
export const DESIGN_RAW_QS_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  ...RAW_QS_CEDED_CASH_FLOW_TABLE_COLUMNS,
  {
    id: 'pmiersRelief',
    label: 'PMIERS Relief',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'costOfPmiers',
    label: 'Cost of PMIERS',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]

export const SUBMISSION_RAW_QS_CEDED_CASH_FLOW_TABLE_COLUMNS: SortTableColumnDef[] = [
  ...RAW_QS_CEDED_CASH_FLOW_TABLE_COLUMNS,
  {
    id: 'roracMargin',
    label: 'RORAC Margin',
    valueType: 'short-numeric',
    editable: false,
    minWidth: '7.5rem',
  },
  {
    id: 'rorac',
    label: 'RORAC',
    valueType: 'percentage',
    editable: false,
    minWidth: '7.5rem',
  },
]
